import { useEffect, useState } from 'react'

import { ClickIds, getClickIdsWithUrlFallback } from 'src/utils/utms'

/**
 * Mount-gated read of the stored ad-platform click IDs (gclid / fbclid /
 * msclkid) for embedding in a WhatsApp prefill URL.
 *
 * Why a hook (and not a bare `getClickIdsFromCookies()` at render): cookies
 * are present on the FIRST client render but absent during SSR, so reading
 * them at render time and feeding the result into an SSR'd `<a href>` would
 * diverge between server and client HTML → React 19 hydration error #418
 * (see the long hydration history in CLAUDE.md). Returning `{}` until after
 * mount keeps SSR and the first client render identical; the click IDs fold
 * in on the post-mount re-render, which is a normal state update React does
 * not compare against the server HTML.
 *
 * Use this in components that build the prefill at RENDER time and put it in
 * an href (SpecialistCard, WrapperWA, PreAuthReservar). Click-handler call
 * sites (PriceCard, Header, LeadFormInlineCta, Flotillas) run only in the
 * browser on user interaction, so they can call `getClickIdsFromCookies()`
 * directly without this hook.
 *
 * Reads with a URL fallback (2026-10-06): this effect runs BEFORE `<UTMs />`
 * stores the cookies on the landing page view, so a cookie-only read shipped
 * the floating WA button's prefill without the gclid for anyone who clicked
 * on the page they landed on — and the gclid is the only way a WA lead
 * reaches Google Ads.
 */
export function useClickIdsForPrefill(): ClickIds {
  const [clickIds, setClickIds] = useState<ClickIds>({})

  useEffect(() => {
    setClickIds(getClickIdsWithUrlFallback())
  }, [])

  return clickIds
}
